/**
 * 토큰화 유틸리티
 * 텍스트를 검색 가능한 토큰으로 분해
 */

/**
 * 텍스트를 토큰으로 분해
 */
export function tokenize(text: string): string[] {
  // YAML frontmatter 제거
  const textWithoutFrontmatter = removeYamlFrontmatter(text)
  
  // 마크다운 문법 제거
  const cleanText = removeMarkdownSyntax(textWithoutFrontmatter)
  
  // 소문자 변환
  const lowerText = cleanText.toLowerCase()
  
  // 단어 추출 (영문, 한글, 숫자 지원)
  const tokens = lowerText.match(/[a-z0-9]+|[가-힣]+/g) || []
  
  // 중복 제거 및 필터링
  const uniqueTokens = new Set<string>()
  
  for (const token of tokens) {
    // 너무 짧은 토큰 제외 (1글자)
    if (token.length > 1) {
      // 불용어 필터링
      if (!isStopWord(token)) {
        uniqueTokens.add(token)
        
        // n-gram 생성 (선택적)
        if (token.length > 4) {
          const ngrams = generateNgrams(token, 2, 3)
          ngrams.forEach(gram => uniqueTokens.add(gram))
        }
      }
    }
  }
  
  return Array.from(uniqueTokens)
}

/**
 * YAML frontmatter 제거
 */
function removeYamlFrontmatter(text: string): string {
  // Check if the text starts with ---
  if (!text.startsWith('---\n') && !text.startsWith('---\r\n')) {
    return text
  }
  
  const lines = text.split('\n')
  
  // Find the closing --- for frontmatter
  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === '---' || lines[i].trim() === '---') {
      // Return everything after the closing ---
      return lines.slice(i + 1).join('\n')
    }
  }
  
  // If no closing --- found, return original text
  return text
}

/**
 * 마크다운 문법 제거
 */
function removeMarkdownSyntax(text: string): string {
  let clean = text
  
  // 코드 블록 제거
  clean = clean.replace(/```[\s\S]*?```/g, ' ')
  clean = clean.replace(/`[^`]*`/g, ' ')
  
  // 링크 텍스트만 추출
  clean = clean.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  
  // 이미지 제거
  clean = clean.replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
  
  // 헤딩 마커 제거
  clean = clean.replace(/^#{1,6}\s+/gm, '')
  
  // 리스트 마커 제거
  clean = clean.replace(/^[\*\-\+]\s+/gm, '')
  clean = clean.replace(/^\d+\.\s+/gm, '')
  
  // 인용 마커 제거
  clean = clean.replace(/^>\s+/gm, '')
  
  // 굵게, 기울임 등 제거
  clean = clean.replace(/[\*_]{1,3}([^\*_]+)[\*_]{1,3}/g, '$1')
  
  // HTML 태그 제거
  clean = clean.replace(/<[^>]+>/g, ' ')
  
  // 특수문자를 공백으로
  clean = clean.replace(/[^\w\s가-힣]/g, ' ')
  
  // 연속된 공백 정리
  clean = clean.replace(/\s+/g, ' ')
  
  return clean.trim()
}

/**
 * 불용어 체크
 */
function isStopWord(word: string): boolean {
  const stopWords = new Set([
    // 영어 불용어
    'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have',
    'i', 'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you',
    'do', 'at', 'this', 'but', 'his', 'by', 'from', 'they',
    'we', 'say', 'her', 'she', 'or', 'an', 'will', 'my', 'one',
    'all', 'would', 'there', 'their', 'what', 'so', 'up', 'out',
    'if', 'about', 'who', 'get', 'which', 'go', 'me', 'when',
    
    // 한국어 불용어
    '이', '그', '저', '것', '수', '등', '및', '를', '을', '가',
    '이다', '있다', '하다', '되다', '없다', '않다', '그리고',
    '하지만', '그러나', '그래서', '따라서', '즉', '만약', '만일',
    '아니다', '또한', '또', '더', '덜', '매우', '아주', '너무'
  ])
  
  return stopWords.has(word)
}

/**
 * N-gram 생성
 */
function generateNgrams(text: string, minSize: number, maxSize: number): string[] {
  const ngrams: string[] = []
  
  for (let n = minSize; n <= maxSize && n <= text.length; n++) {
    for (let i = 0; i <= text.length - n; i++) {
      ngrams.push(text.substring(i, i + n))
    }
  }
  
  return ngrams
}

/**
 * 검색 쿼리 토큰화
 */
export function tokenizeQuery(query: string): string[] {
  // 일반 토큰화
  const tokens = tokenize(query)
  
  // 원본 쿼리도 포함 (정확한 매칭용)
  const cleanQuery = query.toLowerCase().trim()
  if (cleanQuery.length > 1 && cleanQuery.length < 50) {
    tokens.push(cleanQuery)
  }
  
  return tokens
}

/**
 * 퍼지 매칭을 위한 토큰 유사도 계산
 */
export function calculateTokenSimilarity(token1: string, token2: string): number {
  if (token1 === token2) return 1.0
  
  // 레벤슈타인 거리 계산
  const distance = levenshteinDistance(token1, token2)
  const maxLength = Math.max(token1.length, token2.length)
  
  // 유사도 점수 (0-1)
  return 1 - (distance / maxLength)
}

/**
 * 레벤슈타인 거리 계산
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = []
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i]
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // 치환
          matrix[i][j - 1] + 1,     // 삽입
          matrix[i - 1][j] + 1      // 삭제
        )
      }
    }
  }
  
  return matrix[str2.length][str1.length]
}

/**
 * 토큰 하이라이팅
 */
export function highlightTokens(text: string, tokens: string[]): string {
  let highlighted = text
  
  for (const token of tokens) {
    const regex = new RegExp(`\\b(${escapeRegex(token)})\\b`, 'gi')
    highlighted = highlighted.replace(regex, '<mark>$1</mark>')
  }
  
  return highlighted
}

/**
 * 정규표현식 이스케이프
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}